/*  MUI  */
import { Typography } from '@mui/material';

/*  Shared  */
import { Response, SessionHelper } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/*  Objects  */
import { Tenant } from 'objects/Tenant';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

/*  Components  */
import ObjectPicker from './Components/ObjectPicker';

export const TenantPicker = (props) => {
    const loadTenants = async ({
        global,
        partnerTenant,
        session,
        setTenants
    }) => {
        const sessionHelper = new SessionHelper(session);
        const tenant = partnerTenant || sessionHelper.getTenant();
        const { id, internalId } = tenant;

        const params = { excludeDisabled: true, name: '*', size: -1 };
        if (global) {
            params.global = true;
        } else {
            params.includeClients = true;
            // We should only try excluding the partner tenant if the viewing tenant IS a partner.
            params.excludePartner = tenant.partner ? id !== internalId : false;
        }

        let response;
        try {
            const { CancelToken } = axios;
            const source = CancelToken.source();
            response = await axios.get('/tenant', {
                cancelToken: source.token,
                params
            });
            if (!Response.hasData(response)) {
                return;
            }
        } catch (e) {
            debug.e('Error getting Tenants', e);
            return;
        }

        const data = Response.getData(response);
        const tenants = data.sort((a, b) => `${a.name}`.localeCompare(b.name));
        setTenants(tenants);
    };

    const makeObjectPayload = ({
        allowAdd,
        setLoaded,
        setObjectPayload,
        tenants
    }) => {
        setLoaded(false);
        if (!Array.isArray(tenants)) {
            return;
        }

        const objectPayload = [];
        if (allowAdd) {
            const name = '--- NEW CLIENT ---';
            const tenant = { id: 'new', name };
            objectPayload.push({
                ...tenant,
                componentToDisplay: (
                    <Tenant data={tenant}>
                        <div style={{ width: '100%' }}>{name}</div>
                    </Tenant>
                )
            });
        }

        tenants.forEach((tenant) => {
            const { partner } = tenant;
            let { name } = tenant;

            if (!name) {
                return;
            }

            let text = name;
            if (partner) {
                name += ' - Internal Deployment';
                text = (
                    <Typography variant="span" fontWeight={600}>
                        {name}
                    </Typography>
                );
            }

            objectPayload.push({
                ...tenant,
                componentToDisplay: (
                    <Tenant data={tenant}>
                        <div style={{ width: '100%' }}>{text}</div>
                    </Tenant>
                ),
                name
            });
        });

        setObjectPayload(objectPayload);
        setLoaded(true);
    };

    const [tenants, setTenants] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [objectPayload, setObjectPayload] = useState([]);

    const { partnerId } = useParams();
    const partnerTenantInfo = useSelector((state) => state.tenants[partnerId]);
    const partnerTenant = partnerTenantInfo?.tenant;

    const { allowAdd, allValue, disabled, global, reload, value, variant } =
        props;

    const session = useSession();

    useEffect(() => {
        async function fetchData() {
            await loadTenants({ global, partnerTenant, session, setTenants });
        }
        fetchData();
    }, [global, reload, session, partnerTenant]);

    useEffect(() => {
        makeObjectPayload({ allowAdd, setLoaded, setObjectPayload, tenants });
    }, [allowAdd, allValue, tenants, value]);

    return (
        <ObjectPicker
            data-testid="tenantPickerObjectPicker"
            {...props}
            loaded={loaded}
            disabled={disabled}
            objectPayload={objectPayload}
            objectNameField="name"
            objectNameFieldVisualOnly="componentToDisplay"
            variant={variant}
            visualOnlyDefault={undefined}
        />
    );
};
